import { AUTH_ERROR_MESSAGE } from 'shared'
import { useAuthStore } from '@/stores/auth'
import { logout } from './apiClients/auth'
import { API_BASE_URL } from './env'

/** 認証不要なAPIパス。これ以外はtokenを自動付与する。 */
const NO_AUTH_PATHS = ['/api/login', '/api/login/token']

/**
 * APIエラーレスポンス型
 */
export interface ApiErrorResponse {
  message?: string
  error?: string
}

/**
 * APIエラークラス
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: ApiErrorResponse,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * APIリクエストヘルパー
 * fetchをラップしてエラーハンドリングとJSON処理を統一
 */
let isRedirecting = false

export async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  // 相対URLの場合、API_BASE_URLを追加
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`

  // 外部URLおよび認証不要パス以外にtokenを自動付与
  const isExternalUrl = url.startsWith('http')
  const needsAuth = !isExternalUrl && !NO_AUTH_PATHS.includes(url)
  const authHeaders: Record<string, string> = {}
  if (needsAuth) {
    const token = useAuthStore.getState().getToken()
    if (!token) {
      throw new ApiError(AUTH_ERROR_MESSAGE, 401)
    }
    authHeaders.Authorization = `Bearer ${token}`
  }

  try {
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
        ...authHeaders,
      },
    })

    // レスポンスボディを取得
    const contentType = response.headers.get('content-type')
    const hasJsonContent = contentType?.includes('application/json')

    let data: ApiErrorResponse | T | null = null
    if (hasJsonContent) {
      data = (await response.json()) as T | ApiErrorResponse
    }

    // セッション切れの検知: 401 → ログアウト → ログイン画面へリダイレクト
    if (response.status === 401) {
      if (!isRedirecting) {
        isRedirecting = true
        try {
          await logout()
        } catch {
          isRedirecting = false
        }
      }
      throw new ApiError('Session expired', 401, data as ApiErrorResponse)
    }

    // エラーレスポンスの処理
    if (!response.ok) {
      const errorMessage =
        (data as ApiErrorResponse)?.message || (data as ApiErrorResponse)?.error || `HTTP ${response.status}`

      throw new ApiError(errorMessage, response.status, data as ApiErrorResponse)
    }

    return data as T
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    // ネットワークエラーなど
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0, // status 0 = network error
    )
  }
}

// ============================================
// 各APIモジュールからのre-export
// import { xxx } from '@/lib' で使用可能
// ============================================

export type { OpportunityListResponse } from 'backend/types'
// アカウント系
export { changePassword, getAccount } from './apiClients/account'
// アクションログ系
export { getOpportunityActionLogs } from './apiClients/action-logs'
// 認証系
export { login, logout, validateToken } from './apiClients/auth'
export type { CommentMarkersResponse } from './apiClients/comments'
// コメント系
export {
  createOpportunityComment,
  deleteOpportunityComment,
  getCommentMarkers,
  getOpportunityComments,
  updateOpportunityComment,
} from './apiClients/comments'
export type {
  OpportunityIncentive,
  OpportunityIncentiveAccept,
  OpportunityIncentiveAcceptsResponse,
  OpportunityIncentivesResponse,
} from './apiClients/incentives'
// インセンティブ系
export {
  acceptOpportunityIncentive,
  getMemberOpportunityIncentiveAccepts,
  getMemberOpportunityIncentives,
} from './apiClients/incentives'
export type {
  CreateInquiryAssignRequest,
  CreateInquiryCoordinationRequest,
  UpdateInquiryCoordinationRequest,
} from './apiClients/inquiries'
// 引合系
export {
  createInquiryAssign,
  createInquiryCoordination,
  deleteInquiryCoordination,
  getInquiries,
  getInquiryActionLogs,
  getInquiryAssigns,
  getInquiryCoordinations,
  getInquirySales,
  updateInquiryAssign,
  updateInquiryCoordination,
} from './apiClients/inquiries'
export type {
  Contact,
  CreateInquiryRelatedDivisionRequest,
  CreateInquiryRequest,
  CreateInquiryResponse,
  Division,
  GetContactsResponse,
  GetDivisionsResponse,
  GetInquiryRoleMembersParams,
  GetInquiryRoleMembersResponse,
  InquiryRoleMember,
  UpdateInquiryRequest,
} from './apiClients/inquiry'
export {
  createInquiry,
  createInquiryRelatedDivision,
  deleteInquiryRelatedDivision,
  getContacts,
  getDivisions,
  getInquiryRoleMembers,
  updateInquiry,
} from './apiClients/inquiry'
// マスタ系
export { getShiftServices } from './apiClients/master'
export type { CreateOpportunityData, Opportunity } from './apiClients/opportunities'
// 案件系
export { createOpportunity, getOpportunities, getOpportunity, updateOpportunity } from './apiClients/opportunities'
export type {
  CloseReason,
  GetCloseReasonsResponse,
  GetOpportunityStatusesResponse,
  OpportunityStatus,
  UpdateStatusData,
  UpdateStatusResponse,
} from './apiClients/opportunity-status'
// ステータス系
export { getCloseReasons, getOpportunityStatuses, updateOpportunityStatus } from './apiClients/opportunity-status'
// 売上系
export { getMemberMonthlySales, getOpportunityMonthlySales, getOpportunitySummary } from './apiClients/sales'
export type {
  ChatMember,
  GetChatMembersResponse,
  GetSalesforceCustomersResponse,
  GetSalesforceDealAnosResponse,
  GetSalesforceDealsResponse,
  GetSalesforceProjectsResponse,
  SalesforceCustomer,
  SalesforceDeal,
  SalesforceDealAno,
  SalesforceProject,
} from './apiClients/salesforce'
// Salesforce系
export {
  getChatMembers,
  getSalesforceCustomers,
  getSalesforceDealAnos,
  getSalesforceDeals,
  getSalesforceProjectMembers,
  getSalesforceProjects,
} from './apiClients/salesforce'

// 公開案件系
export { getSharableOpportunities, getSharableOpportunityDetail } from './apiClients/sharable'
export type { GetAllTagsResponse, GetTagsResponse, OpportunityTag, UpdateTagsResponse } from './apiClients/tags'
// タグ系
export { getAllTags, getOpportunityTags, updateOpportunityTags } from './apiClients/tags'
export type { OpportunityTask } from './apiClients/tasks'
// タスク系
export {
  createOpportunityTask,
  deleteOpportunityTask,
  getMyTasks,
  getOpportunityTasks,
  getTaskStatuses,
  updateOpportunityTask,
} from './apiClients/tasks'
